import { Utils } from './Utils';

/**
 * 玩家态文案占位符的唯一尺（2026-10-16 茄子令「收成一把尺」）。
 *
 * 由头：`NPCs.ts` 罗一鸣的 `firstMeet` 写了 `%玩家名%`（10-15 数据导入笔 `d357a9e`），
 * 但这个记号过去只有评价库 `ReviewSystem.fill()` 认——偶遇/探班那条台词链（`EncounterSystem` → VN）
 * 一枚都不替，于是屏上直接播出字面 `%玩家名%`。同一个记号两处各写各的替换，就是这个洞的形状。
 *
 * 口径两条（用户同日拍板）：
 *  - **评价库**：取不到依赖数据就整条跳过（`fillTokens` 返回 null，老纪律一字未改，评价里不许残留 `%`）；
 *  - **偶遇/探班台词**：整条撤就是一张空卡，所以取不到时**原样留句并 console.warn**
 *    （`fillTokensLoud`，点名是哪句、缺哪枚——兜底要响，不许静默把配错播出门）。
 *
 * 记号清单不许就地扩（沿 2026-10-02「不许扩占位符」与 10-06 那条「除玩家名之外仍不许扩，要加先问」）。
 * `KNOWN_TOKENS` 是全库已登记清单，读表哨兵 `web/tests/_check_text_tokens.mjs` 拿它对账：
 * 文案表里冒出没登记的记号＝上线前红（不许让玩家当探针）。
 *
 * 本文件不许 import systems/（core 反向依赖 systems 会成环），所以 ctx 由调用方现算传进来。
 */

/** 玩家态记号（本尺负责替换的五枚），键名＝记号本体：`%玩家名%` ↔ ctx.玩家名 */
export interface PlayerTokens {
    玩家名?: string;
    作品名?: string;
    角色名?: string;
    咖位名?: string;
    粉丝数?: string;
}

export const PLAYER_TOKEN_KEYS: (keyof PlayerTokens)[] = ['玩家名', '作品名', '角色名', '咖位名', '粉丝数'];

/**
 * 全库已登记记号＝读表哨兵的对账清单。`PLAYER_TOKEN_KEYS` 那五枚由本文件替，其余各有归属：
 *  - `奖项名`：`AwardSpeeches.fill()`（获奖感言，颁奖夜现算的当届上下文）
 *  - `works`/`age`/`fans`/`rank`/`awards`：`EndingSystem`（结局页履历模板，收官时一次算好）
 *  - `s`：`CharactersPanel` 送礼回话池（旧 `%s` 写法，沿用未改）
 *  - `{player}`（花括号形态）：剧情插值与小助理台词共用（昵称优先）——剧情侧 `plotStage`、小助理侧
 *    `AssistantSystem.replacePlayer`（2026-10-25 台词批：默认不拼名，写才拼）；两者同记号同语义各管各链。
 *  - `~`（行首，小助理台词私有记号）：拼昵称标记——`AssistantSystem.joinTerm` 剥记号并在句首拼「昵称，」
 *    （空昵称不拼直接正文；~ 不出现在玩家文案里。2026-10-28 台词表令）。
 * 加新记号＝先改这份清单并点名由谁替，再往表里写；顺序反了哨兵会红。
 */
export const KNOWN_TOKENS: string[] = [...PLAYER_TOKEN_KEYS, '奖项名', 'works', 'age', 'fans', 'rank', 'awards', 's'];

/**
 * 记号本体只认「字母/数字/下划线/汉字」夹在两个 `%` 之间——
 * 文案里成对的百分号（如「30%~50%」）中间夹着别的字符，不许被当成记号误伤。
 */
const TOKEN_RE = /%([A-Za-z0-9_\u4e00-\u9fa5]+)%/g;

/** 句子里出现的记号（去重）。哨兵、替换、warn 三处共用这把尺，不许别处再写一遍正则 */
export function tokensIn(text: string): string[] {
    const found: string[] = [];
    for (const m of text.matchAll(TOKEN_RE)) if (!found.includes(m[1])) found.push(m[1]);
    return found;
}

/**
 * 现算玩家态 ctx（评价库与偶遇/探班同吃，省得两处各读各的字段）。
 * 作品名/角色名本尺不知道，调用方用 `extra` 带上（评价库带近作，偶遇带在拍的剧）。
 */
export function playerTokens(p: { name?: string; rank?: number; fans?: number }, rankName?: string, extra?: PlayerTokens): PlayerTokens {
    return {
        玩家名: p.name || undefined,
        咖位名: rankName || undefined,
        粉丝数: p.fans !== undefined ? Utils.formatCountCN(p.fans) : undefined,
        ...extra,
    };
}

function swap(text: string, key: string, value: string): string {
    return text.split(`%${key}%`).join(value);
}

/**
 * 替一遍玩家态记号。句子里用到的记号只要有任一不归本尺管或取不到值就返回 null
 * （评价库纪律：整条跳过，不许用空串顶名、也不许让 `%` 残在玩家看得见的评价里）。
 */
export function fillTokens(text: string, ctx: PlayerTokens): string | null {
    let out = text;
    for (const key of tokensIn(text)) {
        if (!PLAYER_TOKEN_KEYS.includes(key as keyof PlayerTokens)) return null;
        const v = ctx[key as keyof PlayerTokens];
        if (!v) return null;
        out = swap(out, key, v);
    }
    return out;
}

/**
 * 台词用（偶遇/探班/初遇）：取不到不撤句——整条撤了就是一张空卡。
 * 原样播出并 `console.warn` 点名缺哪枚（用户定「原样留着＋warn」），配错本身由读表哨兵拦在上线前。
 */
export function fillTokensLoud(text: string, ctx: PlayerTokens, where: string): string {
    let out = text;
    for (const key of tokensIn(text)) {
        if (!PLAYER_TOKEN_KEYS.includes(key as keyof PlayerTokens)) {
            console.warn(`[TextTokens] ${where} 的文案用了不归本尺管的记号 %${key}%，原样上屏（登记清单见 core/TextTokens.KNOWN_TOKENS）`);
            continue;
        }
        const v = ctx[key as keyof PlayerTokens];
        if (!v) {
            console.warn(`[TextTokens] ${where} 的文案要 %${key}%，但当前取不到，原样上屏`);
            continue;
        }
        out = swap(out, key, v);
    }
    return out;
}
